import { BrowserRouter, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import store from './store/store';
import AppRoutes from './AppRoutes';
import { getNewToken } from './services/authApi';
import { bookmarksApi } from './services/bookmarksApi';
import { materialsApi } from './services/materialsApi';
import { xpApi } from './services/xpApi';
import { useOnlineSocket } from './hooks/useOnlineSocket';
import { useGetNotificationsQuery } from './services/notificationsApi';
import { setUnreadCount } from './features/notificationsSlice';

// Декодирует payload JWT без проверки подписи — нужен только exp, чтобы
// не слать заведомо просроченный access_token в первый запрос после захода на сайт
// (бэкенд на /materials использует OptionalAuth и молча отдаёт ответ без user_meta
// вместо 401, поэтому RTK Query reauth-логика не успевает сработать вовремя).
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return false;
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AppContent = () => {
  const theme = useSelector((state) => state.ui.theme);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const dispatch = useDispatch();
  useOnlineSocket();

  // Загружаем начальный unread count при авторизации
  const { data: notifData } = useGetNotificationsQuery(
    { page: 1, perPage: 1 },
    { skip: !isAuthenticated }
  );
  useEffect(() => {
    if (notifData?.data?.unread_count !== undefined) {
      dispatch(setUnreadCount(notifData.data.unread_count));
    }
  }, [notifData, dispatch]);

  useEffect(() => {
    document.body.classList.toggle('light-theme', theme === 'light');
    document.body.style.background = '';
  }, [theme]);

  const prevIsAuthenticated = useRef(null);

  useEffect(() => {
    const wasAuthenticated = prevIsAuthenticated.current;
    prevIsAuthenticated.current = isAuthenticated;

    if (wasAuthenticated === false && isAuthenticated) {
      // Только переход false → true (свежий логин)
      dispatch(bookmarksApi.util.invalidateTags(['Bookmarks']));
      dispatch(materialsApi.util.invalidateTags(['Materials']));
      // Grant daily login XP — idempotent, DB unique index prevents duplicates per day
      dispatch(xpApi.endpoints.dailyLogin.initiate());
    } else if (wasAuthenticated === true && !isAuthenticated) {
      // true → false: logout — сбрасываем кэш с user_meta
      dispatch(materialsApi.util.invalidateTags(['Materials']));
      dispatch(bookmarksApi.util.resetApiState());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('access_token');
      const savedUser = localStorage.getItem('user');

      // Токен есть, но истёк (например после 1-2 дней без захода на сайт) —
      // обновляем его в фоне. UI рендерится сразу со старым токеном (как раньше),
      // а после успешного refresh инвалидируем кэш материалов/закладок, чтобы
      // карусель и каталог перезапросили данные уже с user_meta.bookmark
      // (бэкенд на /materials использует OptionalAuth и молча отдаёт ответ без
      // user_meta вместо 401, поэтому обычная reauth-логика RTK Query не успевает).
      if (savedToken && savedUser) {
        if (isTokenExpired(savedToken)) {
          try {
            await getNewToken();
            dispatch(materialsApi.util.invalidateTags(['Materials']));
            dispatch(bookmarksApi.util.invalidateTags(['Bookmarks']));
          } catch {
            // refresh не удался — logout уже вызван внутри getNewToken
          }
        }
        return;
      }

      // Нет токена в localStorage — пробуем восстановить сессию только если
      // пользователь был залогинен раньше (auth_session убирается при явном logout)
      const hadSession = localStorage.getItem('auth_session');
      if (!hadSession) return;

      try {
        await getNewToken();
      } catch {
        // сессия истекла — нормально
      }
    };

    initAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppRoutes />
    </BrowserRouter>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <Provider store={store}>
        <AppContent />
      </Provider>
    </HelmetProvider>
  );
};

export default App;
